import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, IndianRupee, Leaf, Package, PhoneCall, ShieldCheck, Truck } from 'lucide-react';
import DecorLeaf from './DecorLeaf';
import { STORE } from '../config/store';
import { homeRail, focusRing } from '../styles/ui';

// The four wholesale propositions the site already published, in the approved
// design's wording. Plain statements of what the service is — no volume,
// client count or delivery guarantee.
const BENEFITS = [
  {
    icon: IndianRupee,
    title: 'Competitive Pricing',
    description: 'Get the best rates on bulk orders and increase your profit margins.',
    tint: 'bg-[#DCFCE7] text-[#15803D]',
    line: '#4CAF50',
  },
  {
    icon: Package,
    title: 'Bulk Supply',
    description: 'Regular supply of high-quality products in large quantities.',
    tint: 'bg-[#FFEDD5] text-[#EA580C]',
    line: '#F59E0B',
  },
  {
    icon: Truck,
    title: 'Timely Delivery',
    description: 'Reliable and on-time delivery to keep your business running smoothly.',
    tint: 'bg-[#DBEAFE] text-[#2563EB]',
    line: '#60A5FA',
  },
  {
    icon: ShieldCheck,
    title: 'Quality Assured',
    description: 'Fresh, hygienic and carefully inspected products for your trust.',
    tint: 'bg-[#FCE7F3] text-[#E11D48]',
    line: '#F472B6',
  },
];

// The closing strip of the wholesale band. The reference shows partner counts
// and a delivery-network reach; this project publishes neither, so the strip
// carries the promises the site already makes (from the cards above and the
// former "Why choose us" section) rather than invented statistics.
const PROMISES = [
  { icon: BadgeCheck, stat: '100%', title: 'Quality Checked' },
  { icon: Leaf, stat: 'Fresh Supply', title: 'Every Day' },
  { icon: Truck, stat: 'Timely Delivery', title: 'Reliable & on time' },
  { icon: ShieldCheck, stat: 'Quality Assured', title: 'Strict quality control' },
];

const Wholesale = () => (
  <>
    <section
      id="wholesale"
      aria-labelledby="wholesale-heading"
      className="relative overflow-hidden pt-5 lg:pt-[23px] pb-[17px] bg-[linear-gradient(180deg,#F6FBF3_0%,#EEF7EA_45%,#F5FAF2_100%)]"
    >
      {/* Edge artwork from the approved design: the produce crate with its
          handwritten note on the left, the wholesale supply scene on the right.
          Both are decoration only. */}
      <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
        <img src="/home/wholesale-crate.webp" alt="" loading="lazy" width="345" height="435" className="hidden xl:block absolute left-0 top-[10px] w-[345px]" />
        <img src="/home/wholesale-truck.webp" alt="" loading="lazy" width="341" height="460" className="hidden xl:block absolute right-0 top-[-5px] w-[341px]" />
        <DecorLeaf className="hidden xl:block absolute left-[300px] top-[100px] w-9 text-emerald-500/80 rotate-[24deg]" />
        <DecorLeaf className="hidden xl:block absolute right-[420px] top-[50px] w-9 text-emerald-500/80 -rotate-[30deg]" />
        <DecorLeaf className="hidden xl:block absolute right-[360px] top-[100px] w-6 text-emerald-600/70 rotate-[10deg]" />
        <DecorLeaf className="hidden xl:block absolute left-[250px] bottom-[60px] w-7 text-emerald-600/70 -rotate-[16deg]" />
      </div>

      <div className={`${homeRail} relative`}>
        <div className="text-center max-w-[640px] mx-auto">
          <span className="inline-flex items-center gap-1.5 h-[27px] px-4 rounded-full bg-white border border-emerald-100 text-emerald-800 text-[10.5px] font-bold tracking-[0.12em] uppercase shadow-[0_1px_3px_rgba(15,51,24,0.08)]">
            <DecorLeaf className="w-3 text-emerald-600" />
            Grow Together
          </span>
          <h2 id="wholesale-heading" className="serif mt-[13px] text-[32px] sm:text-[40px] xl:text-[46px] font-bold text-emerald-950 leading-[1.05]">
            Wholesale <span className="text-[#1E8A3B]">Partnership</span>
          </h2>
          <p className="mt-[12px] text-[15px] xl:text-[16.5px] leading-[22px] text-[#4B5563]">
            Partner with us for bulk supply of fresh fish, dairy, meat, and grocery essentials at
            the best prices. Grow your business with trusted quality and reliable delivery.
          </p>
        </div>

        <div className="mt-[23px] xl:px-[194px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-[14px] items-stretch">
            {BENEFITS.map(({ icon: Icon, title, description, tint, line }) => (
              <div
                key={title}
                className="relative h-full min-h-[184px] rounded-2xl bg-white border border-[#E6EEE6] shadow-[0_10px_24px_-14px_rgba(15,51,24,0.25)] px-[20px] pt-[19px] pb-5 overflow-hidden"
              >
                <svg className="absolute right-3 top-3 w-[140px] h-[60px]" viewBox="0 0 140 60" fill="none" aria-hidden="true">
                  <path d="M0 58 C 60 56, 100 40, 132 8" stroke={line} strokeWidth="1.2" strokeDasharray="1 4" strokeLinecap="round" />
                  <circle cx="133" cy="7" r="3" fill={line} />
                </svg>
                <span className={`w-[60px] h-[60px] rounded-full ${tint} flex items-center justify-center`}>
                  <Icon size={26} strokeWidth={1.8} aria-hidden="true" />
                </span>
                <h3 className="serif mt-[22px] text-[19px] font-bold text-emerald-950 leading-tight">{title}</h3>
                <p className="mt-[8px] text-[13px] text-[#4B5563] leading-[1.45]">{description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Real destinations: the contact section below (enquiries) and the
            store's published phone number. */}
        <div className="mt-[18px] flex flex-wrap items-center justify-center gap-[15px]">
          <Link
            to="/#contact"
            className={`inline-flex items-center gap-2 h-[38px] px-8 rounded-full bg-[linear-gradient(90deg,#3FA84A,#2E8B47)] text-white text-[13.5px] font-bold shadow-[0_8px_18px_-6px_rgba(46,139,71,0.55)] hover:brightness-105 ${focusRing}`}
          >
            Partner With Us
            <ArrowRight size={15} strokeWidth={2.5} aria-hidden="true" />
          </Link>
          <a
            href={STORE.phoneHref}
            className={`inline-flex items-center gap-2 h-[38px] px-7 rounded-full bg-white border border-[#2E8B47] text-[#1E5A30] text-[13.5px] font-bold hover:bg-emerald-50 ${focusRing}`}
          >
            <PhoneCall size={15} aria-hidden="true" />
            Know More
          </a>
        </div>
      </div>
    </section>

    <section aria-label="Our wholesale promises" className="bg-white">
      <div className={homeRail}>
        <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-0 xl:divide-x xl:divide-[#E3ECE3] py-[22px] xl:px-[44px]">
          {PROMISES.map(({ icon: Icon, stat, title }) => (
            <li key={title} className="flex items-center gap-3 xl:px-8 first:xl:pl-0">
              <span className="w-[42px] h-[42px] rounded-[12px] bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
                <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
              </span>
              <span className="leading-tight">
                <span className="block text-[15px] font-bold text-emerald-950">{stat}</span>
                <span className="block text-[12.5px] text-ink-secondary mt-[3px]">{title}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  </>
);

export default Wholesale;
